import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { FileText, Download, CreditCard, Banknote, Building2 } from 'lucide-react';
import { useLanguage } from '../translations/LanguageContext';

const Contract = () => {
  const { t, language } = useLanguage();

  // Map language to contract file
  const contractFiles = {
    en: '/contracts/Contract_EN.pdf',
    bg: '/contracts/Contract_BG.pdf',
    ru: '/contracts/Contract_RU.pdf',
  };

  const currentContract = contractFiles[language] || contractFiles.en;

  return (
    <div className="min-h-screen bg-[#2e5453]">
      {/* Hero Section */}
      <section className="bg-[#3d6a68] py-16">
        <div className="container mx-auto px-4 text-center">
          <Badge className="bg-[#96ba1e] text-[#2e5453] hover:bg-[#a8c82e] mb-4">
            {t('contract.badge')}
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-[#96ba1e]">
            {t('contract.title')}
          </h1>
          <p className="text-xl text-[#c4d88f] max-w-3xl mx-auto">
            {t('contract.subtitle')}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-[#2e5453]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
            
            {/* Contract Download Section */}
            <Card className="bg-[#3d6a68] border-[#96ba1e] border">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-[#2e5453] rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-[#96ba1e]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#96ba1e] mb-2">
                    {t('contract.downloadTitle')}
                  </h2>
                  <p className="text-[#c4d88f]">
                    {t('contract.downloadDesc')}
                  </p>
                </div>

                <a 
                  href={currentContract} 
                  download
                  className="block"
                >
                  <Button className="w-full bg-[#96ba1e] hover:bg-[#a8c82e] text-[#2e5453] font-bold py-6 text-lg">
                    <Download className="w-5 h-5 mr-2" />
                    {t('contract.downloadButton')}
                  </Button>
                </a>

                <div className="mt-6 pt-6 border-t border-[#96ba1e]/30">
                  <p className="text-[#c4d88f] text-sm text-center mb-4">
                    {t('contract.otherLanguages')}
                  </p>
                  <div className="flex justify-center gap-3">
                    <a href="/contracts/Contract_EN.pdf" download>
                      <Button variant="outline" size="sm" className="border-[#96ba1e] text-[#96ba1e] hover:bg-[#2e5453]">
                        English
                      </Button>
                    </a>
                    <a href="/contracts/Contract_BG.pdf" download>
                      <Button variant="outline" size="sm" className="border-[#96ba1e] text-[#96ba1e] hover:bg-[#2e5453]">
                        Български
                      </Button>
                    </a>
                    <a href="/contracts/Contract_RU.pdf" download>
                      <Button variant="outline" size="sm" className="border-[#96ba1e] text-[#96ba1e] hover:bg-[#2e5453]">
                        Русский
                      </Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Information Section */}
            <Card className="bg-[#3d6a68] border-[#96ba1e] border">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-[#2e5453] rounded-full flex items-center justify-center mx-auto mb-4">
                    <CreditCard className="w-8 h-8 text-[#96ba1e]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#96ba1e] mb-2">
                    {t('contract.paymentTitle')}
                  </h2>
                  <p className="text-[#c4d88f]">
                    {t('contract.paymentDesc')}
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Payment Methods */}
                  <div className="bg-[#2e5453] rounded-lg p-4 border border-[#96ba1e]/30">
                    <h3 className="font-bold text-[#96ba1e] mb-3 flex items-center">
                      <Banknote className="w-5 h-5 mr-2" />
                      {t('contract.acceptedMethods')}
                    </h3>
                    <ul className="space-y-2 text-[#c4d88f]">
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-[#96ba1e] rounded-full mr-3"></span>
                        {t('contract.cash')}
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-[#96ba1e] rounded-full mr-3"></span>
                        {t('contract.bankTransfer')}
                      </li>
                    </ul>
                  </div>

                  {/* Down Payment Info */}
                  <div className="bg-[#2e5453] rounded-lg p-4 border border-[#96ba1e]/30">
                    <h3 className="font-bold text-[#96ba1e] mb-3 flex items-center">
                      <Building2 className="w-5 h-5 mr-2" />
                      {t('contract.downPaymentTitle')}
                    </h3>
                    <div className="text-[#c4d88f]">
                      <p className="mb-2">{t('contract.downPaymentDesc')}</p>
                      <div className="bg-[#3d6a68] rounded p-3 mt-3">
                        <span className="text-3xl font-bold text-[#96ba1e]">50%</span>
                        <span className="text-[#c4d88f] ml-2">{t('contract.downPaymentAmount')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Note */}
                  <div className="text-center text-[#c4d88f] text-sm pt-4">
                    <p>{t('contract.paymentNote')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Additional Info */}
          <div className="max-w-4xl mx-auto mt-8">
            <Card className="bg-[#3d6a68] border-[#96ba1e] border">
              <CardContent className="p-6">
                <p className="text-[#c4d88f] text-center">
                  {t('contract.questions')} <a href="/contact" className="text-[#96ba1e] hover:underline font-semibold">{t('contract.contactUs')}</a>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contract;
